import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, PermissionsProvider} from '@/components';
import {useCountryList} from '@/context/CountryListContext';
import type {BusinessOnboarding} from '@/types';
import {BusinessOnboardingStatus} from '@/types';
import {mapCountryListToOptions} from '@/utils/countries';
import {capitalizeWords} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

const SummaryRow = ({
  label,
  value,
}: {
  label: string;
  value: string | number | undefined | null;
}) => (
  <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
    <p className="flex-[0.4] text-slate-500 text-sm dark:text-slate-100">
      {label}
    </p>
    <p className="flex-[0.6] text-sm font-medium text-gray-700 dark:text-gray-100">
      {value ?? '-'}
    </p>
  </div>
);

interface InvitesSummaryProps {
  invite: BusinessOnboarding;
  onEdit: (invite: BusinessOnboarding) => void;
  onDeny: (invite: BusinessOnboarding) => void;
}

const InvitesSummary = ({invite, onEdit, onDeny}: InvitesSummaryProps) => {
  const isApproved = invite.meta?.status === BusinessOnboardingStatus.APPROVED;
  const {state: countryListState} = useCountryList();
  const countryOptions = mapCountryListToOptions(countryListState.countryList);

  const country = invite.businessCountry
    ? (countryOptions[invite.businessCountry] ?? invite.businessCountry)
    : undefined;
  const volume =
    invite.businessVolume != null
      ? `$${invite.businessVolume.toLocaleString()}`
      : undefined;

  return (
    <div className="bg-white dark:bg-transparent rounded-lg w-full px-5 py-5">
      <SummaryRow label="Business Name" value={invite.businessName} />
      <SummaryRow label="Full Name" value={invite.name} />
      <SummaryRow label="Email" value={invite.email} />
      <SummaryRow label="Country" value={country} />
      <SummaryRow
        label="Industry"
        value={capitalizeWords(invite.businessIndustry || '-')}
      />
      <SummaryRow label="Current Monthly Volume" value={volume} />
      <SummaryRow
        label="Phone"
        value={invite.businessPhone?.value || undefined}
      />
      <SummaryRow
        label="Applied On"
        value={formatDate.DDMonYYYY(invite.createdAt || '')}
      />
      {isApproved && (
        <SummaryRow
          label="Approved On"
          value={formatDate.DDMonYYYY(invite.updatedAt || '')}
        />
      )}

      <PermissionsProvider
        permission={AfriexAdminPermission.BusinessOnboardingManage}
      >
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="outline"
            className="text-xs px-3 py-1 h-8"
            onClick={() => onEdit(invite)}
          >
            Edit Invite
          </Button>
          {/* Pending rows already carry Deny on the row itself. */}
          {isApproved && (
            <Button
              colorScheme="red"
              className="text-xs px-3 py-1 h-8"
              onClick={() => onDeny(invite)}
            >
              Deny
            </Button>
          )}
        </div>
      </PermissionsProvider>
    </div>
  );
};

export default InvitesSummary;
